import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Upload, Save, Send, Eye, X, Search, Sparkles, ExternalLink, Eraser } from "lucide-react";

const CATEGORIES = [
  ["news", "News"], ["camp_results", "Camp Results"], ["rankings", "Rankings"],
  ["commitments", "Commitments"], ["player_spotlights", "Player Spotlights"],
  ["cbr_nation", "CBR Nation"], ["announcements", "Announcements"],
];
const NEXT_STATUS = {
  draft: [["pending_review", "Submit for Review"]],
  pending_review: [["approved", "Approve"], ["draft", "Back to Draft"]],
  approved: [["published", "Publish"], ["scheduled", "Schedule"]],
  scheduled: [["published", "Publish Now"]],
  published: [["archived", "Archive"]],
  archived: [["draft", "Restore to Draft"]],
};
const QUILL_MODULES = { toolbar: [[{ header: [2, 3, false] }], ["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["blockquote", "link", "image"], ["clean"]], clipboard: { matchVisual: false } };

// Strips inline styles/classes and converts hard <br> line breaks (common when
// pasting from ChatGPT/Word) into soft-flowing paragraphs so text wraps naturally.
function cleanArticleHtml(html) {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  doc.body.querySelectorAll("br").forEach((br) => br.replaceWith(" "));
  doc.body.querySelectorAll("span, font, div").forEach((el) => el.replaceWith(...el.childNodes));
  doc.body.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((at) => {
      if (!["href", "src", "alt"].includes(at.name)) el.removeAttribute(at.name);
    });
  });
  return doc.body.innerHTML.replace(/[ \t]{2,}/g, " ").replace(/(<p>\s*<\/p>)+/g, "").trim();
}

export default function ArticleEditor() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const coverInput = useRef();
  const ogInput = useRef();
  const [playerQuery, setPlayerQuery] = useState("");
  const [playerResults, setPlayerResults] = useState([]);
  const [camps, setCamps] = useState([]);

  const load = useCallback(() => {
    api.get(`/admin/articles/${articleId}`).then(({ data }) => setA(data)).catch(() => toast.error("Article not found"));
  }, [articleId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/camps", { params: { status_filter: "" } }).then(({ data }) => setCamps(data.items || [])).catch(() => {}); }, []);

  const set = (patch) => setA((prev) => ({ ...prev, ...patch }));
  const setSeo = (patch) => setA((prev) => ({ ...prev, seo: { ...(prev.seo || {}), ...patch } }));

  const save = async (silent = false) => {
    setBusy(true);
    try {
      const payload = {
        title: a.title, subtitle: a.subtitle, category: a.category, body_html: a.body_html,
        cover_image_url: a.cover_image_url, video_url: a.video_url, seo: a.seo,
        associations: a.associations, scheduled_at: a.scheduled_at,
      };
      await api.patch(`/admin/articles/${articleId}`, payload);
      if (!silent) toast.success("Saved");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const uploadImage = async (file, target) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (target === "cover") set({ cover_image_url: data.url });
      if (target === "og") setSeo({ og_image_url: data.url });
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed"); }
  };

  const changeStatus = async (status) => {
    await save(true);
    try {
      await api.post(`/admin/articles/${articleId}/status`, { status });
      toast.success(`Moved to ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  const del = async () => {
    try { await api.delete(`/admin/articles/${articleId}`); toast.success("Deleted"); navigate("/admin/cms"); }
    catch (e) { toast.error(e.message); }
  };

  const searchPlayers = async (q) => {
    setPlayerQuery(q);
    if (q.length < 2) { setPlayerResults([]); return; }
    const { data } = await api.get("/players", { params: { q, limit: 6 } });
    setPlayerResults(data.items || []);
  };
  const addPlayer = (p) => {
    const cur = a.associations?.player_ids || [];
    if (!cur.includes(p.id)) set({ associations: { ...a.associations, player_ids: [...cur, p.id] } });
    const det = a.associations_detail || { players: [], camps: [], awards: [] };
    setA((prev) => ({ ...prev, associations_detail: { ...det, players: [...(det.players || []).filter((x) => x.id !== p.id), p] } }));
    setPlayerQuery(""); setPlayerResults([]);
  };
  const removePlayer = (pid) => set({ associations: { ...a.associations, player_ids: (a.associations?.player_ids || []).filter((x) => x !== pid) } });
  const toggleCamp = (cid) => {
    const cur = a.associations?.camp_ids || [];
    set({ associations: { ...a.associations, camp_ids: cur.includes(cid) ? cur.filter((x) => x !== cid) : [...cur, cid] } });
  };

  if (!a) return <AdminLayout><div className="p-8 label-overline">Loading…</div></AdminLayout>;
  const assocPlayers = (a.associations_detail?.players || []).filter((p) => (a.associations?.player_ids || []).includes(p.id));

  return (
    <AdminLayout>
      <div className="p-4 sm:p-8 max-w-6xl">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link to="/admin/cms" className="label-overline text-slate-500 hover:text-[#E63946] inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> All Articles</Link>
          <div className="flex items-center gap-2">
            {a.is_auto_generated && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#E63946]/10 text-[#E63946] px-2 py-1 rounded-sm"><Sparkles className="w-3 h-3" /> Auto-generated draft</span>}
            <span className="text-[10px] uppercase tracking-wider bg-slate-100 px-2 py-1 rounded-sm">{a.status}</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-4">
          {/* Main editor */}
          <div className="lg:col-span-2 space-y-4">
            <input data-testid="article-title" value={a.title} onChange={(e) => set({ title: e.target.value })} placeholder="Article title"
              className="w-full font-display text-2xl uppercase tracking-tight px-3 py-2 border border-slate-300 rounded-sm" />
            <input data-testid="article-subtitle" value={a.subtitle || ""} onChange={(e) => set({ subtitle: e.target.value })} placeholder="Subtitle (optional)"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            <div className="bg-white border border-slate-300 rounded-sm" data-testid="article-body">
              <div className="flex items-center justify-end px-2 py-1.5 border-b border-slate-200 bg-slate-50">
                <button
                  data-testid="article-clean-formatting"
                  onClick={() => { set({ body_html: cleanArticleHtml(a.body_html) }); toast.success("Formatting cleaned"); }}
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-[#E63946] transition-colors"
                >
                  <Eraser className="w-3.5 h-3.5" /> Clean Formatting
                </button>
              </div>
              <ReactQuill theme="snow" value={a.body_html || ""} onChange={(v) => set({ body_html: v })} modules={QUILL_MODULES} />
            </div>
            <div>
              <label className="label-overline text-[10px]">Embedded Video URL (YouTube/Vimeo)</label>
              <input data-testid="article-video" value={a.video_url || ""} onChange={(e) => set({ video_url: e.target.value })} placeholder="https://youtube.com/watch?v=…" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
              <div>
                <label className="label-overline text-[10px]">Category</label>
                <select data-testid="article-category" value={a.category} onChange={(e) => set({ category: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                  {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="label-overline text-[10px]">Cover Image</label>
                {a.cover_image_url && <img src={a.cover_image_url} alt="" className="mt-1 w-full h-28 object-cover rounded-sm border border-slate-200" />}
                <input ref={coverInput} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], "cover")} />
                <button data-testid="article-cover-upload" onClick={() => coverInput.current.click()} className="mt-2 w-full inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-[#E63946] px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><Upload className="w-3.5 h-3.5" /> Upload Cover</button>
              </div>
            </div>

            {/* Associations */}
            <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3" data-testid="article-associations">
              <div className="label-overline">Related Content</div>
              <div>
                <label className="label-overline text-[10px]">Players</label>
                <div className="relative">
                  <input data-testid="assoc-player-search" value={playerQuery} onChange={(e) => searchPlayers(e.target.value)} placeholder="Search players…" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                  {playerResults.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 bg-white border border-slate-200 rounded-sm shadow-lg mt-1 max-h-48 overflow-auto">
                      {playerResults.map((p) => (
                        <button key={p.id} onClick={() => addPlayer(p)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">{p.first_name} {p.last_name} <span className="text-slate-400 text-xs">{p.primary_position} {p.graduation_year}</span></button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {assocPlayers.map((p) => (
                    <span key={p.id} className="inline-flex items-center gap-1 bg-slate-100 text-xs px-2 py-1 rounded-sm">{p.first_name} {p.last_name}<button onClick={() => removePlayer(p.id)}><X className="w-3 h-3" /></button></span>
                  ))}
                </div>
              </div>
              <div>
                <label className="label-overline text-[10px]">Camps</label>
                <div className="mt-1 max-h-32 overflow-auto space-y-1">
                  {camps.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={(a.associations?.camp_ids || []).includes(c.id)} onChange={() => toggleCamp(c.id)} />
                      <span className="truncate">{c.title}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* SEO */}
            <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3" data-testid="article-seo">
              <div className="label-overline">SEO</div>
              <input data-testid="seo-title" value={a.seo?.seo_title || ""} onChange={(e) => setSeo({ seo_title: e.target.value })} placeholder="SEO Title" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              <textarea data-testid="seo-description" value={a.seo?.meta_description || ""} onChange={(e) => setSeo({ meta_description: e.target.value })} placeholder="Meta description" rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              <input data-testid="seo-canonical" value={a.seo?.canonical_url || ""} onChange={(e) => setSeo({ canonical_url: e.target.value })} placeholder="Canonical URL" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              {a.seo?.og_image_url && <img src={a.seo.og_image_url} alt="" className="w-full h-20 object-cover rounded-sm border border-slate-200" />}
              <input ref={ogInput} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], "og")} />
              <button data-testid="og-upload" onClick={() => ogInput.current.click()} className="w-full inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-[#E63946] px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><Upload className="w-3.5 h-3.5" /> Upload OG Image</button>
            </div>

            {/* Actions */}
            <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-2">
              <button data-testid="article-save" onClick={() => save()} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60"><Save className="w-4 h-4" /> Save</button>
              {(NEXT_STATUS[a.status] || []).map(([s, label]) => (
                <button key={s} data-testid={`article-status-${s}`} onClick={() => changeStatus(s)} className="w-full inline-flex items-center justify-center gap-2 bg-[#E63946] hover:bg-[#D90429] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-wider rounded-sm"><Send className="w-4 h-4" /> {label}</button>
              ))}
              {a.status === "published" && <Link to={`/news/${a.slug}`} target="_blank" className="w-full inline-flex items-center justify-center gap-2 border border-slate-300 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider rounded-sm"><Eye className="w-4 h-4" /> View Public</Link>}
              <button data-testid="article-delete" onClick={del} className="w-full text-[11px] text-slate-400 hover:text-[#E63946] uppercase tracking-wider pt-1">Delete Article</button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
